import MenuList from '@components/common/menu/MenuList';
import { buildContextMenuFromType } from '@components/map/contextMenu/builderFactory';
import { HasEventContext, HasMeasureContext, HasQrCodeContext } from '@components/map/contextMenu/builders/common';
import { PerimeterCameraContextMenuContext } from '@components/map/contextMenu/builders/perimeterCamera';
import { FeatureTypeToContextComponentEnum } from '@components/map/contextMenu/type';
import { useMapContext } from '@components/map/MapContext';
import { createErrorToastFromTradId } from '@components/ui/toaster';
import { useAppSelector } from '@hooks/redux.hooks';
import { MeasureOriginEnum, useMeasureFromPointMenuItem } from '@hooks/useMeasureFromPointMenuItem';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { hasRole } from '@redux/authent/authent.selectors';
import { updateSelectedDoubtCheckPerimeterCameraUniqueKey } from '@redux/global/global.reducer';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectPopupsByMapId } from '@redux/maps/maps.selectors';
import { isOpenedPopupByType } from '@utils/popup.utils';
import { getSensorUniqueKey } from '@utils/sensors/sensors.utils';
import { toLngLat } from '@utils/validation/coordinates.utils';
import { useCallback } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { RoleEnum } from '@/types/authent/roles.types';
import { MapIdEnum } from '@/types/map.types';
import { MenuItems } from '@/types/menu.types';
import { EventPopupData, PopupDataType, PopupTypeEnum } from '@/types/popup.types';
import { PerimeterCamera } from '@/types/sensor/sensors/perimeterCamera.types';

interface PerimeterCameraContextMenuProps {
  perimeterCamera: PerimeterCamera;
  clickPosition: { x: number; y: number };
  onClose: () => void;
}

function PerimeterCameraContextMenuContent({
  onClose,
  perimeterCamera,
  clickPosition,
}: Readonly<PerimeterCameraContextMenuProps>) {
  const { mapId } = useMapContext();
  const popups = useSelectorWithMapId(selectPopupsByMapId);
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const selectDoubtCheckPerimeterCamera = useWithDispatch(updateSelectedDoubtCheckPerimeterCameraUniqueKey);
  const accessEventCommand = useAppSelector((state) => hasRole(state, RoleEnum.CREATE_REPLAY));

  const openPopup = (popupDataType: PopupDataType) =>
    updatePopup({
      mapId,
      popup: {
        ...popupDataType,
        position: { x: clickPosition.x, y: clickPosition.y },
      },
    });

  const openGpsQrCodePopup = () => {
    if (perimeterCamera.coordinates) {
      openPopup({
        type: PopupTypeEnum.GPS_QR_CODE,
        data: {
          coordinates: perimeterCamera.coordinates,
        },
      });
      onClose();
    }
  };

  const handleActivate = () => {
    onClose();
    selectDoubtCheckPerimeterCamera(getSensorUniqueKey(perimeterCamera));
  };

  const handleCreateEventClick = useCallback(() => {
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.EVENT,
        position: { x: clickPosition.x, y: clickPosition.y },
        data: {
          newEvent: {
            coordinates: perimeterCamera.coordinates,
            date: new Date().toISOString(),
          },
        } as EventPopupData,
      },
    });
    onClose();
  }, [clickPosition.x, clickPosition.y, mapId, onClose, perimeterCamera.coordinates, updatePopup]);

  const [label, enableMeasure] = useMeasureFromPointMenuItem({
    closeContextMenu: onClose,
    position: perimeterCamera.coordinates,
    measureOrigin: MeasureOriginEnum.CAMERA,
  });

  const measureContext: HasMeasureContext = {
    position: toLngLat(perimeterCamera.coordinates),
    label,
    enableMeasure,
  };
  const eventContext: HasEventContext = {
    popups,
    accessEventCommand,
    isOpenedPopupByType,
    handleCreateEventClick,
  };
  const qrCodeContext: HasQrCodeContext = {
    position: toLngLat(perimeterCamera.coordinates),
    openGpsQrCodePopup,
  };

  const ctx: PerimeterCameraContextMenuContext = {
    handleActivate,
    ...eventContext,
    ...measureContext,
    ...qrCodeContext,
  };

  const menuItems: MenuItems = buildContextMenuFromType<FeatureTypeToContextComponentEnum.PERIMETER_CAMERA>(
    FeatureTypeToContextComponentEnum.PERIMETER_CAMERA,
    mapId,
    ctx,
  );

  return <MenuList menuItems={menuItems} />;
}

export default function PerimeterCameraContextMenu(props: Readonly<PerimeterCameraContextMenuProps>) {
  const { mapId } = useMapContext();

  return mapId === MapIdEnum.REPLAY ? (
    <ErrorBoundary fallback={<></>} onError={() => createErrorToastFromTradId('error.sensorReplay.contextMenu')}>
      <PerimeterCameraContextMenuContent {...props} />
    </ErrorBoundary>
  ) : (
    <PerimeterCameraContextMenuContent {...props} />
  );
}
